import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { queryClient, queryKeys } from '@/api/query-client';
import type { components } from '@/api/generated/schema';

export type ConversationResponse = components['schemas']['PixlideApplicationFeaturesSupportConversationResponse'];
export type MessageResponse = components['schemas']['PixlideApplicationFeaturesSupportMessageResponse'];
export type SendMessageCommand = components['schemas']['PixlideApplicationFeaturesSupportSendConversationMessageCommand'];
export type AttachmentUploadTicket = components['schemas']['PixlideApplicationFeaturesSupportAttachmentUploadTicket'];
export type ArchivedConversationListItem =
  components['schemas']['PixlideApplicationFeaturesSupportArchivedConversationListItem'];

/**
 * Conversation support du tenant courant.
 *
 * `conversationId` est `null` tant qu'aucun message n'a jamais été échangé : c'est un choix
 * de pureté CQRS assumé côté backend (une requête ne crée jamais d'état), pas une erreur.
 * Le rendu doit donc traiter ce cas comme un état vide, pas une erreur.
 */
export function useConversation() {
  return useQuery({
    queryKey: queryKeys.conversation,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/support');

      return data!;
    },
  });
}

/**
 * Envoie un message au support ; crée la conversation du tenant si elle n'existe pas encore.
 *
 * Accepte un texte, une pièce jointe (déjà déposée via `useCreateAttachmentUploadTicket`), ou
 * les deux — mais jamais ni l'un ni l'autre, le backend rejette un message totalement vide.
 */
export function useSendMessage() {
  return useMutation({
    mutationFn: async (body: SendMessageCommand) => {
      const { data } = await api.POST('/api/v1/support/messages', { body });

      return data!;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation });
    },
  });
}

/**
 * Émet un ticket de dépôt présigné pour une pièce jointe de message support.
 *
 * Ne dépose rien elle-même : l'appelant doit ensuite `PUT` le fichier sur `uploadUrl` (un
 * stockage objet présigné, hors de l'API Pixlide) avant d'envoyer le message avec `storageKey`.
 */
export function useCreateAttachmentUploadTicket() {
  return useMutation({
    mutationFn: async (file: { fileName: string; contentType: string; sizeBytes: number }) => {
      const { data } = await api.POST('/api/v1/support/attachments/upload-ticket', { body: file });

      return data!;
    },
  });
}

/**
 * Émet une URL de téléchargement présignée (5 minutes) pour la pièce jointe d'un message.
 *
 * Mutation plutôt que query : l'URL est à usage court et ne doit être demandée qu'au moment
 * où le client clique réellement sur la pièce jointe, pas préchargée pour chaque message.
 */
export function useMessageAttachmentDownloadUrl() {
  return useMutation({
    mutationFn: async (messageId: string) => {
      const { data } = await api.POST('/api/v1/support/messages/{messageId}/attachment/download-url', {
        params: { path: { messageId } },
        // FastEndpoints répond 415 sans Content-Type + corps JSON valide, même si le contrat
        // OpenAPI ne déclare aucun corps pour cette route (même remarque que `useMarkConversationRead`).
        // @ts-expect-error — le contrat OpenAPI ne déclare pas de corps pour cette route.
        body: {},
      });

      return data!;
    },
  });
}

/**
 * Démarre explicitement une nouvelle conversation après la fermeture de la précédente.
 * Refusé par le backend si une conversation est déjà en cours (non fermée).
 */
export function useStartNewConversation() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.POST('/api/v1/support/new');

      return data!;
    },

    onSettled: () => {
      // Y compris en échec (409 « conversation.already_open ») : le refus signifie qu'une
      // conversation ouverte existe déjà côté serveur — la vue doit se resynchroniser sur cet
      // état réel plutôt que de rester bloquée sur le badge « fermée » périmé.
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation });
    },
  });
}

/** Marque la conversation support comme lue côté client. */
export function useMarkConversationRead() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/support/read');
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.conversation });
    },
  });
}

/** Page de conversations support fermées (archive) du tenant courant, triées par fermeture décroissante. */
export function useArchivedConversations(page: number, pageSize: number) {
  return useQuery({
    queryKey: queryKeys.archivedConversations({ page, pageSize }),

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/support/archive', {
        params: { query: { page, pageSize } },
      });

      return data!;
    },
  });
}

/** Détail complet (lecture seule) d'une conversation archivée du tenant courant. */
export function useArchivedConversation(conversationId: string | null) {
  return useQuery({
    queryKey: queryKeys.archivedConversation(conversationId ?? ''),
    enabled: conversationId != null,

    queryFn: async () => {
      const { data } = await api.GET('/api/v1/support/archive/{conversationId}', {
        params: { path: { conversationId: conversationId! } },
      });

      return data!;
    },
  });
}

/**
 * « Supprimer tous les messages » (Paramètres › Préférences › Chat) : masque l'historique
 * pour l'utilisateur connecté seulement — le reste de l'équipe et le support Pixlide le
 * conservent.
 */
export function useClearSupportHistory() {
  return useMutation({
    mutationFn: async () => {
      await api.POST('/api/v1/support/history/clear');
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['support'] });
    },
  });
}
